/**
 * DashboardDemandOffersPage
 * 
 * CI Merchant Dashboard: Demand & Offers
 * Route: /dashboard/deals/demand
 * 
 * Tabs: Demand Pools | Offer Requests | Offers | Insights (Coming Soon)
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Search, TrendingUp, Users, Target, Zap, ExternalLink, Download, Plus,
  ChevronRight, Clock, Eye, MousePointerClick, CheckCircle, AlertCircle,
  PauseCircle, XCircle, MoreHorizontal, Copy, RefreshCw, ArrowUpDown,
  BarChart3, Shield, Globe, Lock, Play, Pause, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { MerchantHeader } from '../components/MerchantHeader';
import apiClient from '../services/api';

// =============================================================================
// Helper Components
// =============================================================================

const KPICard = ({ title, value, subtitle, icon: Icon, trend, loading, testId }) => (
  <Card data-testid={testId} className="bg-card border-border/80 rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.25)]">
    <CardContent className="p-6">
      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-16" />
          <Skeleton className="h-3 w-32" />
        </div>
      ) : (
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground font-medium">{title}</p>
            <p className="text-3xl font-bold mt-1 tracking-tight" style={{ fontFamily: "'Space Grotesk', monospace" }}>{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1.5">{subtitle}</p>}
            {trend !== undefined && (
              <div className={`flex items-center gap-1 mt-2 text-xs ${trend >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                <TrendingUp className={`h-3 w-3 ${trend < 0 ? 'rotate-180' : ''}`} />
                <span>{trend >= 0 ? '+' : ''}{trend}% vs last week</span>
              </div>
            )}
          </div>
          {Icon && (
            <div className="p-2.5 rounded-lg bg-primary/10">
              <Icon className="h-5 w-5 text-primary" />
            </div>
          )}
        </div>
      )}
    </CardContent>
  </Card>
);

const StatusBadge = ({ status }) => {
  const config = {
    draft: { color: 'bg-muted text-muted-foreground border-border', icon: null },
    active: { color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
    paused: { color: 'bg-amber-500/10 text-amber-400 border-amber-500/30', icon: PauseCircle },
    expired: { color: 'bg-red-500/10 text-red-400 border-red-500/30', icon: XCircle },
    open: { color: 'bg-primary/10 text-primary border-primary/30', icon: AlertCircle },
    fulfilled: { color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
    'cooling down': { color: 'bg-amber-500/10 text-amber-400 border-amber-500/30', icon: Clock },
  };
  const { color, icon: StatusIcon } = config[status?.toLowerCase()] || config.draft;
  
  return (
    <Badge className={`${color} border text-xs capitalize`}>
      {StatusIcon && <StatusIcon className="h-3 w-3 mr-1" />}
      {status}
    </Badge>
  );
};

const DemandScoreBar = ({ score }) => {
  const getColor = (s) => {
    if (s >= 80) return 'bg-emerald-500';
    if (s >= 60) return 'bg-amber-500';
    if (s >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };
  
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
        <div 
          className={`h-full ${getColor(score)} rounded-full transition-all duration-300`} 
          style={{ width: `${Math.min(score, 100)}%` }} 
        />
      </div>
      <span className="text-xs font-semibold tracking-tight" style={{ fontFamily: "'Space Grotesk', monospace" }}>{score}</span>
    </div>
  );
};

const VisibilityBadge = ({ visibility }) => (
  <Badge variant="outline" className="text-xs capitalize gap-1">
    {visibility === 'watchers' ? <Eye className="h-3 w-3" /> : <Globe className="h-3 w-3" />}
    {visibility === 'watchers' ? 'Watchers' : 'Public'}
  </Badge>
);

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

// =============================================================================
// Demand Pools Table
// =============================================================================

const DemandPoolsTable = ({ pools, loading, onViewPool, onCreateOffer }) => {
  if (loading) {
    return (
      <div className="space-y-3" data-testid="pools-loading-skeleton">
        {[1, 2, 3, 4, 5].map(i => (
          <Skeleton key={i} className="h-16 w-full rounded-lg" />
        ))}
      </div>
    );
  }
  
  if (!pools?.length) {
    return (
      <Card className="bg-card border-border/80 rounded-xl" data-testid="pools-empty-state">
        <CardContent className="p-12 text-center">
          <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-semibold mb-2">No Demand Pools Yet</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            You'll see pools once shoppers track items from your domain on TheDeals.ai. Demand pools aggregate anonymous shopper intent.
          </p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="border border-border/80 rounded-xl overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.25)]" data-testid="pools-table">
      <table className="w-full">
        <thead className="bg-muted/30 border-b border-border">
          <tr>
            <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Pool</th>
            <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Type</th>
            <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Watchers</th>
            <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Target Price</th>
            <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Urgency</th>
            <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Requests (7d)</th>
            <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Last Offer</th>
            <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Demand Score</th>
            <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {pools.map((pool, idx) => (
            <tr 
              key={pool.pool_id} 
              data-testid={`pool-row-${pool.pool_id}`}
              className={`border-b border-border/50 hover:bg-muted/20 transition-colors duration-200 cursor-pointer ${idx % 2 === 0 ? 'bg-card' : 'bg-muted/5'}`}
              onClick={() => onViewPool(pool)}
            >
              <td className="px-4 py-3">
                <div className="text-left">
                  <p className="font-medium text-sm line-clamp-1 hover:text-primary transition-colors duration-200">{pool.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{pool.domain}</p>
                </div>
              </td>
              <td className="px-4 py-3">
                <Badge variant="outline" className="text-xs capitalize">
                  {pool.type}
                </Badge>
              </td>
              <td className="px-4 py-3">
                <span className="text-sm font-semibold tracking-tight" style={{ fontFamily: "'Space Grotesk', monospace" }}>
                  {pool.watchers_bucket || pool.watchers}
                </span>
              </td>
              <td className="px-4 py-3">
                {pool.target_price_median ? (
                  <div>
                    <p className="text-sm font-medium" style={{ fontFamily: "'Space Grotesk', monospace" }}>
                      ${pool.target_price_median.toFixed(2)}
                    </p>
                    {pool.target_price_p25 && pool.target_price_p75 && (
                      <p className="text-xs text-muted-foreground">
                        ${pool.target_price_p25} - ${pool.target_price_p75}
                      </p>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">--</span>
                )}
              </td>
              <td className="px-4 py-3">
                <span className="text-xs text-muted-foreground">{pool.urgency_label || '--'}</span>
              </td>
              <td className="px-4 py-3">
                <span className="text-sm font-semibold tracking-tight" style={{ fontFamily: "'Space Grotesk', monospace" }}>
                  {pool.requests_7d}
                </span>
              </td>
              <td className="px-4 py-3">
                {pool.last_offer_at ? (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">{formatTimeAgo(pool.last_offer_at)}</span>
                    {pool.last_offer_status && <StatusBadge status={pool.last_offer_status} />}
                  </div>
                ) : (
                  <Badge variant="outline" className="text-xs text-muted-foreground border-dashed">None</Badge>
                )}
              </td>
              <td className="px-4 py-3">
                <DemandScoreBar score={pool.demand_score} />
              </td>
              <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-1.5">
                  <Button 
                    size="sm" 
                    variant="outline"
                    data-testid={`pool-view-btn-${pool.pool_id}`}
                    onClick={() => onViewPool(pool)}
                    className="h-7 text-xs"
                  >
                    View
                  </Button>
                  <Button 
                    size="sm"
                    data-testid={`pool-create-offer-btn-${pool.pool_id}`}
                    onClick={() => onCreateOffer(pool)}
                    className="h-7 text-xs"
                  >
                    Create Offer
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                        <MoreHorizontal className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => { navigator.clipboard.writeText(pool.pool_id); toast.success('Pool ID copied'); }}>
                        <Copy className="h-4 w-4 mr-2" />
                        Copy Pool ID
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Download className="h-4 w-4 mr-2" />
                        Export Pool
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// =============================================================================
// Offer Requests Table
// =============================================================================

const OfferRequestsTable = ({ requests, loading, onCreateOffer }) => {
  if (loading) {
    return (
      <div className="space-y-3" data-testid="requests-loading-skeleton">
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full rounded-lg" />)}
      </div>
    );
  }
  
  if (!requests?.length) {
    return (
      <Card className="bg-card border-border/80 rounded-xl" data-testid="requests-empty-state">
        <CardContent className="p-12 text-center">
          <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-semibold mb-2">No Offer Requests</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Offer requests appear when shoppers actively ask for better prices on tracked items.
          </p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="border border-border/80 rounded-xl overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.25)]" data-testid="requests-table">
      <table className="w-full">
        <thead className="bg-muted/30 border-b border-border">
          <tr>
            <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Pool</th>
            <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Requests</th>
            <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Median Target</th>
            <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Top Timeframe</th>
            <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Allow Alternatives</th>
            <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Last Request</th>
            <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Status</th>
            <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Action</th>
          </tr>
        </thead>
        <tbody>
          {requests.map((req, idx) => (
            <tr key={req.request_id} data-testid={`request-row-${req.request_id}`} className={`border-b border-border/50 hover:bg-muted/20 transition-colors duration-200 ${idx % 2 === 0 ? 'bg-card' : 'bg-muted/5'}`}>
              <td className="px-4 py-3">
                <p className="font-medium text-sm">{req.pool_title}</p>
              </td>
              <td className="px-4 py-3">
                <span className="text-sm font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', monospace" }}>{req.requests_count}</span>
              </td>
              <td className="px-4 py-3">
                <span className="text-sm" style={{ fontFamily: "'Space Grotesk', monospace" }}>
                  {req.median_target_price ? `$${req.median_target_price.toFixed(2)}` : '--'}
                </span>
              </td>
              <td className="px-4 py-3">
                <span className="text-sm">{req.top_timeframe || '--'}</span>
              </td>
              <td className="px-4 py-3">
                <span className="text-sm">{req.allow_alternatives_pct !== null && req.allow_alternatives_pct !== undefined ? `${req.allow_alternatives_pct}%` : '--'}</span>
              </td>
              <td className="px-4 py-3">
                <span className="text-xs text-muted-foreground">
                  {formatTimeAgo(req.last_request_at) || '--'}
                </span>
              </td>
              <td className="px-4 py-3">
                <StatusBadge status={req.status} />
              </td>
              <td className="px-4 py-3">
                <Button size="sm" className="h-7 text-xs" data-testid={`request-create-offer-btn-${req.request_id}`} onClick={() => onCreateOffer({ pool_id: req.pool_id, title: req.pool_title })}>
                  Create Offer
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// =============================================================================
// Offers Table
// =============================================================================

const OffersTable = ({ offers, loading, onViewOffer, onPauseOffer, onResumeOffer }) => {
  if (loading) {
    return (
      <div className="space-y-3" data-testid="offers-loading-skeleton">
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full rounded-lg" />)}
      </div>
    );
  }
  
  if (!offers?.length) {
    return (
      <Card className="bg-card border-border/80 rounded-xl" data-testid="offers-empty-state">
        <CardContent className="p-12 text-center">
          <Zap className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-semibold mb-2">No Offers Yet</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Create your first offer to start reaching interested shoppers on TheDeals.ai.
          </p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="border border-border/80 rounded-xl overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.25)]" data-testid="offers-table">
      <table className="w-full">
        <thead className="bg-muted/30 border-b border-border">
          <tr>
            <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Offer</th>
            <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Visibility</th>
            <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Status</th>
            <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Schedule</th>
            <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Caps</th>
            <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Performance</th>
            <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {offers.map((offer, idx) => (
            <tr 
              key={offer.offer_id} 
              data-testid={`offer-row-${offer.offer_id}`}
              className={`border-b border-border/50 hover:bg-muted/20 transition-colors duration-200 cursor-pointer ${idx % 2 === 0 ? 'bg-card' : 'bg-muted/5'}`}
              onClick={() => onViewOffer(offer)}
            >
              <td className="px-4 py-3">
                <div>
                  <p className="font-medium text-sm">{offer.discount_text}</p>
                  {offer.coupon_code && (
                    <Badge variant="outline" className="text-xs font-mono mt-1 gap-1">
                      <Lock className="h-2.5 w-2.5" />
                      {offer.coupon_code}
                    </Badge>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">{offer.pool_title}</p>
                </div>
              </td>
              <td className="px-4 py-3">
                <VisibilityBadge visibility={offer.visibility} />
              </td>
              <td className="px-4 py-3">
                <StatusBadge status={offer.status} />
              </td>
              <td className="px-4 py-3">
                <div className="text-xs space-y-0.5">
                  {offer.starts_at && <p>From: {new Date(offer.starts_at).toLocaleDateString()}</p>}
                  {offer.expires_at && (
                    <div className="flex items-center gap-1">
                      <span>To: {new Date(offer.expires_at).toLocaleDateString()}</span>
                      {new Date(offer.expires_at) > new Date() && offer.status === 'active' && (
                        <Badge variant="outline" className="text-[10px] h-4 px-1 border-amber-500/30 text-amber-400">
                          ends {formatTimeAgo(offer.expires_at) ? `in ${Math.ceil((new Date(offer.expires_at) - new Date()) / 86400000)}d` : ''}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="text-xs space-y-0.5" style={{ fontFamily: "'Space Grotesk', monospace" }}>
                  {offer.inventory_cap && <p>{offer.inventory_cap} units</p>}
                  <p>{offer.per_user_cap} per user</p>
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="text-xs space-y-0.5" style={{ fontFamily: "'Space Grotesk', monospace" }}>
                  <p className="flex items-center gap-1"><Eye className="h-3 w-3 text-muted-foreground" />{(offer.impressions || 0).toLocaleString()}</p>
                  <p className="flex items-center gap-1"><MousePointerClick className="h-3 w-3 text-muted-foreground" />{(offer.clicks || 0).toLocaleString()}</p>
                  <p className="text-primary font-semibold">CTR {((offer.ctr || 0) * 100).toFixed(1)}%</p>
                </div>
              </td>
              <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`offer-actions-btn-${offer.offer_id}`}>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {offer.thedeals_deal_url && (
                      <DropdownMenuItem onClick={() => window.open(offer.thedeals_deal_url, '_blank')}>
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Preview on TheDeals
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => onViewOffer(offer)}>
                      <Eye className="h-4 w-4 mr-2" />
                      View Details
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {offer.status === 'active' && (
                      <DropdownMenuItem onClick={() => onPauseOffer(offer.offer_id)}>
                        <Pause className="h-4 w-4 mr-2" />
                        Pause Offer
                      </DropdownMenuItem>
                    )}
                    {offer.status === 'paused' && (
                      <DropdownMenuItem onClick={() => onResumeOffer(offer.offer_id)}>
                        <Play className="h-4 w-4 mr-2" />
                        Resume Offer
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => { navigator.clipboard.writeText(offer.offer_id); toast.success('Offer ID copied'); }}>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy Offer ID
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// =============================================================================
// Pool Details Drawer
// =============================================================================

const PoolDetailsDrawer = ({ pool, open, onClose, onCreateOffer }) => {
  if (!pool) return null;
  
  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-[520px] sm:max-w-[520px] overflow-y-auto bg-card border-l border-border" data-testid="pool-details-drawer">
        <SheetHeader className="pb-4 border-b border-border mb-6">
          <SheetTitle className="text-lg">{pool.title}</SheetTitle>
          <SheetDescription className="flex items-center gap-2 mt-1">
            <span>{pool.domain}</span>
            <Badge variant="outline" className="text-xs">{pool.watchers_bucket || pool.watchers} watchers</Badge>
            <Badge variant="outline" className="text-xs capitalize">{pool.type}</Badge>
          </SheetDescription>
        </SheetHeader>
        
        <div className="space-y-6">
          {/* Overview KPIs */}
          <div>
            <h4 className="text-sm font-medium mb-3 text-muted-foreground uppercase tracking-wider">Overview</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-4 rounded-lg bg-muted/30 border border-border/50">
                <p className="text-xs text-muted-foreground mb-1">Demand Score</p>
                <p className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', monospace" }}>{pool.demand_score}</p>
                <DemandScoreBar score={pool.demand_score} />
              </div>
              <div className="p-4 rounded-lg bg-muted/30 border border-border/50">
                <p className="text-xs text-muted-foreground mb-1">Requests (7d)</p>
                <p className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', monospace" }}>{pool.requests_7d}</p>
                {pool.urgency_label && <p className="text-xs text-muted-foreground mt-1">{pool.urgency_label}</p>}
              </div>
            </div>
          </div>
          
          {/* Price Intelligence */}
          {pool.target_price_median && (
            <div>
              <h4 className="text-sm font-medium mb-3 text-muted-foreground uppercase tracking-wider">Price Intelligence</h4>
              <div className="p-4 rounded-lg bg-muted/30 border border-border/50 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Target Price (Median)</span>
                  <span className="text-lg font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', monospace" }}>${pool.target_price_median.toFixed(2)}</span>
                </div>
                {pool.target_price_p25 && pool.target_price_p75 && (
                  <div className="flex justify-between items-center text-xs text-muted-foreground">
                    <span>Range (p25 - p75)</span>
                    <span>${pool.target_price_p25} - ${pool.target_price_p75}</span>
                  </div>
                )}
                {pool.pct_with_target_price && (
                  <div className="flex justify-between items-center text-xs text-muted-foreground">
                    <span>Watchers with target price</span>
                    <span>{pool.pct_with_target_price}%</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Suggested Offer */}
          <div>
            <h4 className="text-sm font-medium mb-3 text-muted-foreground uppercase tracking-wider">Suggested Offer</h4>
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary/10 mt-0.5">
                    <BarChart3 className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{pool.suggested_discount_band ? `Recommended discount: ${pool.suggested_discount_band}` : 'Recommended discount: 15-25%'}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {pool.suggested_copy || 'Based on watcher target prices and urgency signals'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* CTA */}
          <div className="pt-4 border-t border-border">
            <Button className="w-full" data-testid="drawer-create-offer-btn" onClick={() => { onClose(); onCreateOffer(pool); }}>
              <Plus className="h-4 w-4 mr-2" />
              Create Offer for This Pool
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// =============================================================================
// Offer Details Drawer
// =============================================================================

const OfferDetailsDrawer = ({ offer, open, onClose, onPause, onResume }) => {
  if (!offer) return null;
  
  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-[520px] sm:max-w-[520px] overflow-y-auto bg-card border-l border-border" data-testid="offer-details-drawer">
        <SheetHeader className="pb-4 border-b border-border mb-6">
          <div className="flex items-center gap-2">
            <SheetTitle className="text-lg">{offer.discount_text}</SheetTitle>
            <StatusBadge status={offer.status} />
          </div>
          <SheetDescription className="flex items-center gap-2 mt-1">
            <span>{offer.pool_title}</span>
            <VisibilityBadge visibility={offer.visibility} />
          </SheetDescription>
        </SheetHeader>
        
        <div className="space-y-6">
          {/* Actions */}
          <div className="flex gap-2">
            {offer.status === 'active' && (
              <Button variant="outline" size="sm" onClick={() => { onPause(offer.offer_id); onClose(); }} data-testid="offer-pause-btn">
                <Pause className="h-4 w-4 mr-1" /> Pause
              </Button>
            )}
            {offer.status === 'paused' && (
              <Button variant="outline" size="sm" onClick={() => { onResume(offer.offer_id); onClose(); }} data-testid="offer-resume-btn">
                <Play className="h-4 w-4 mr-1" /> Resume
              </Button>
            )}
            {offer.thedeals_deal_url && (
              <Button variant="outline" size="sm" onClick={() => window.open(offer.thedeals_deal_url, '_blank')} data-testid="offer-view-thedeals-btn">
                <ExternalLink className="h-4 w-4 mr-1" /> View on TheDeals
              </Button>
            )}
          </div>
          
          {/* Offer Config */}
          <div>
            <h4 className="text-sm font-medium mb-3 text-muted-foreground uppercase tracking-wider">Offer Configuration</h4>
            <div className="p-4 rounded-lg bg-muted/30 border border-border/50 space-y-2.5">
              {offer.coupon_code && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Coupon Code</span>
                  <Badge variant="outline" className="font-mono">{offer.coupon_code}</Badge>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Landing URL</span>
                <a href={offer.landing_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline truncate max-w-[200px]">{offer.landing_url}</a>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Schedule</span>
                <span className="text-xs">
                  {offer.starts_at && `${new Date(offer.starts_at).toLocaleDateString()} - `}
                  {offer.expires_at ? new Date(offer.expires_at).toLocaleDateString() : 'No end date'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Caps</span>
                <span className="text-xs">{offer.inventory_cap ? `${offer.inventory_cap} units / ` : ''}{offer.per_user_cap} per user</span>
              </div>
            </div>
          </div>
          
          {/* Performance */}
          <div>
            <h4 className="text-sm font-medium mb-3 text-muted-foreground uppercase tracking-wider">Performance</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-muted/30 border border-border/50">
                <p className="text-xs text-muted-foreground">Impressions</p>
                <p className="text-xl font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', monospace" }}>{(offer.impressions || 0).toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/30 border border-border/50">
                <p className="text-xs text-muted-foreground">Clicks</p>
                <p className="text-xl font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', monospace" }}>{(offer.clicks || 0).toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/30 border border-border/50">
                <p className="text-xs text-muted-foreground">CTR</p>
                <p className="text-xl font-bold tracking-tight text-primary" style={{ fontFamily: "'Space Grotesk', monospace" }}>{((offer.ctr || 0) * 100).toFixed(2)}%</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/30 border border-border/50">
                <p className="text-xs text-muted-foreground">Redeem Intents</p>
                <p className="text-xl font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', monospace" }}>{(offer.redeem_intents || 0).toLocaleString()}</p>
              </div>
            </div>
          </div>
          
          {/* Timestamps */}
          <div>
            <h4 className="text-sm font-medium mb-3 text-muted-foreground uppercase tracking-wider">Timeline</h4>
            <div className="p-4 rounded-lg bg-muted/30 border border-border/50 space-y-2">
              <div className="flex justify-between items-center text-xs">
                <span className="text-muted-foreground">Created</span>
                <span>{offer.created_at ? new Date(offer.created_at).toLocaleString() : '--'}</span>
              </div>
              {offer.published_at && (
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted-foreground">Published</span>
                  <span>{new Date(offer.published_at).toLocaleString()}</span>
                </div>
              )}
              {offer.thedeals_deal_id && (
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted-foreground">TheDeals ID</span>
                  <Badge variant="outline" className="font-mono text-[10px]">{offer.thedeals_deal_id}</Badge>
                </div>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// =============================================================================
// Offer Builder Wizard Modal
// =============================================================================

const OfferBuilderWizard = ({ open, onClose, initialPool, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    pool_id: '',
    pool_title: '',
    offer_type: 'coupon',
    discount_text: '',
    coupon_code: '',
    landing_url: '',
    starts_at: '',
    expires_at: '',
    terms: '',
    visibility: 'watchers',
    min_pool_size: 10,
    inventory_cap: '',
    per_user_cap: 1,
    notify_watchers: true,
    send_to_agents: true,
    agent_channels: ['telegram', 'x'],
    hook_text: '',
    cta_text: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  
  useEffect(() => {
    if (open) {
      setStep(1);
      setValidationErrors({});
      setFormData(prev => ({
        ...prev,
        pool_id: initialPool?.pool_id || initialPool?.pool_id || '',
        pool_title: initialPool?.title || initialPool?.pool_title || ''
      }));
    }
  }, [open, initialPool]);
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors(prev => { const next = { ...prev }; delete next[field]; return next; });
    }
  };
  
  const validateStep2 = () => {
    const errors = {};
    if (!formData.discount_text.trim()) errors.discount_text = 'Required';
    if (!formData.landing_url.trim()) errors.landing_url = 'Required';
    if (formData.landing_url && !formData.landing_url.startsWith('https://')) errors.landing_url = 'Must start with https://';
    if (!formData.expires_at) errors.expires_at = 'Required';
    if (formData.starts_at && formData.expires_at && new Date(formData.starts_at) >= new Date(formData.expires_at)) {
      errors.expires_at = 'End date must be after start date';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleNext = () => {
    if (step === 2 && !validateStep2()) return;
    setStep(s => Math.min(s + 1, 5));
  };
  const handleBack = () => setStep(s => Math.max(s - 1, 1));
  
  const handlePublish = async () => {
    setSubmitting(true);
    try {
      const createRes = await apiClient.post('/api/dashboard/thedeals/offers', formData);
      
      if (createRes.data.ok) {
        const publishRes = await apiClient.post(
          `/api/dashboard/thedeals/offers/${createRes.data.offer_id}/publish`,
          { confirm: true }
        );
        
        if (publishRes.data.ok) {
          toast.success('Offer published successfully', { description: 'Your offer is now visible to eligible shoppers on TheDeals.ai' });
          onSuccess?.();
          onClose();
        } else {
          toast.error('Publish failed', { description: publishRes.data.error || 'Unknown error' });
        }
      } else {
        toast.error('Create failed', { description: createRes.data.error || 'Unknown error' });
      }
    } catch (err) {
      const is503 = err.response?.status === 503;
      toast.error(
        is503 ? 'TheDeals unavailable' : 'Error publishing offer', 
        { description: is503 ? 'Integration is currently offline. Your offer was not created.' : (err.response?.data?.detail || err.message) }
      );
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleSaveDraft = async () => {
    setSubmitting(true);
    try {
      const res = await apiClient.post('/api/dashboard/thedeals/offers', formData);
      if (res.data.ok) {
        toast.success('Draft saved', { description: 'You can publish it later from the Offers tab' });
        onSuccess?.();
        onClose();
      } else {
        toast.error('Save failed', { description: res.data.error || 'Unknown error' });
      }
    } catch (err) {
      const is503 = err.response?.status === 503;
      toast.error(
        is503 ? 'TheDeals unavailable' : 'Error saving draft', 
        { description: is503 ? 'Integration is currently offline. Draft was not saved.' : (err.response?.data?.detail || err.message) }
      );
    } finally {
      setSubmitting(false);
    }
  };
  
  const stepLabels = ['Pool', 'Offer', 'Audience', 'Distribution', 'Review'];
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="offer-builder-wizard">
        <DialogHeader>
          <DialogTitle>Create Offer</DialogTitle>
          <DialogDescription>
            Publish a targeted offer to TheDeals.ai shoppers
          </DialogDescription>
        </DialogHeader>
        
        {/* Stepper */}
        <div className="flex items-center justify-between mb-6 px-2">
          {stepLabels.map((label, idx) => (
            <div key={label} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors duration-200 ${
                step > idx + 1 ? 'bg-primary text-primary-foreground' :
                step === idx + 1 ? 'bg-primary text-primary-foreground shadow-md shadow-primary/30' :
                'bg-muted text-muted-foreground'
              }`}>
                {step > idx + 1 ? <CheckCircle className="h-4 w-4" /> : idx + 1}
              </div>
              <span className={`ml-2 text-xs hidden sm:inline ${step === idx + 1 ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                {label}
              </span>
              {idx < 4 && <ChevronRight className="h-4 w-4 mx-2 text-muted-foreground/50" />}
            </div>
          ))}
        </div>
        
        {/* Step Content */}
        <div className="min-h-[300px]">
          {step === 1 && (
            <div className="space-y-4" data-testid="wizard-step-pool">
              <div>
                <Label>Selected Pool</Label>
                <div className="p-4 rounded-lg bg-muted/30 border border-border/50 mt-2">
                  {formData.pool_title ? (
                    <>
                      <p className="font-medium">{formData.pool_title}</p>
                      <p className="text-xs text-muted-foreground mt-1">Pool ID: {formData.pool_id}</p>
                    </>
                  ) : (
                    <p className="text-muted-foreground text-sm">No pool selected. The offer will be a general offer.</p>
                  )}
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                The offer will target watchers of this demand pool on TheDeals.ai.
              </p>
            </div>
          )}
          
          {step === 2 && (
            <div className="space-y-4" data-testid="wizard-step-offer">
              <div>
                <Label>Offer Type</Label>
                <Select value={formData.offer_type} onValueChange={(v) => handleChange('offer_type', v)}>
                  <SelectTrigger className="mt-2" data-testid="offer-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="coupon">Coupon Code</SelectItem>
                    <SelectItem value="price_drop">Price Drop</SelectItem>
                    <SelectItem value="bundle">Bundle Deal</SelectItem>
                    <SelectItem value="gift">Gift with Purchase</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Discount Text *</Label>
                <Input 
                  data-testid="discount-text-input"
                  placeholder="e.g., 20% off, $30 off"
                  value={formData.discount_text}
                  onChange={(e) => handleChange('discount_text', e.target.value)}
                  className={`mt-2 ${validationErrors.discount_text ? 'border-red-500' : ''}`}
                />
                {validationErrors.discount_text && <p className="text-xs text-red-400 mt-1">{validationErrors.discount_text}</p>}
              </div>
              <div>
                <Label>Coupon Code (optional)</Label>
                <Input 
                  data-testid="coupon-code-input"
                  placeholder="e.g., SAVE20"
                  value={formData.coupon_code}
                  onChange={(e) => handleChange('coupon_code', e.target.value.toUpperCase())}
                  className="mt-2 font-mono"
                />
              </div>
              <div>
                <Label>Landing URL *</Label>
                <Input 
                  data-testid="landing-url-input"
                  placeholder="https://yourstore.com/product"
                  value={formData.landing_url}
                  onChange={(e) => handleChange('landing_url', e.target.value)}
                  className={`mt-2 ${validationErrors.landing_url ? 'border-red-500' : ''}`}
                />
                {validationErrors.landing_url && <p className="text-xs text-red-400 mt-1">{validationErrors.landing_url}</p>}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date (optional)</Label>
                  <Input 
                    data-testid="start-date-input"
                    type="datetime-local"
                    value={formData.starts_at}
                    onChange={(e) => handleChange('starts_at', e.target.value)}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label>End Date *</Label>
                  <Input 
                    data-testid="end-date-input"
                    type="datetime-local"
                    value={formData.expires_at}
                    onChange={(e) => handleChange('expires_at', e.target.value)}
                    className={`mt-2 ${validationErrors.expires_at ? 'border-red-500' : ''}`}
                  />
                  {validationErrors.expires_at && <p className="text-xs text-red-400 mt-1">{validationErrors.expires_at}</p>}
                </div>
              </div>
              <div>
                <Label>Terms (optional)</Label>
                <Textarea 
                  data-testid="terms-input"
                  placeholder="Any restrictions or terms..."
                  value={formData.terms}
                  onChange={(e) => handleChange('terms', e.target.value)}
                  className="mt-2"
                  rows={2}
                />
              </div>
            </div>
          )}
          
          {step === 3 && (
            <div className="space-y-4" data-testid="wizard-step-audience">
              <div>
                <Label>Visibility</Label>
                <Select value={formData.visibility} onValueChange={(v) => handleChange('visibility', v)}>
                  <SelectTrigger className="mt-2" data-testid="visibility-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="watchers">Watchers Only (Recommended)</SelectItem>
                    <SelectItem value="public">Public (All TheDeals users)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Watchers-only offers are targeted to shoppers already tracking this item. Higher conversion, lower noise.
                </p>
              </div>
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Inventory Cap (optional)</Label>
                  <Input 
                    data-testid="inventory-cap-input"
                    type="number"
                    placeholder="e.g., 100"
                    value={formData.inventory_cap}
                    onChange={(e) => handleChange('inventory_cap', e.target.value ? parseInt(e.target.value) : '')}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label>Per User Cap</Label>
                  <Input 
                    data-testid="per-user-cap-input"
                    type="number"
                    value={formData.per_user_cap}
                    onChange={(e) => handleChange('per_user_cap', parseInt(e.target.value) || 1)}
                    className="mt-2"
                  />
                </div>
              </div>
            </div>
          )}
          
          {step === 4 && (
            <div className="space-y-4" data-testid="wizard-step-distribution">
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/20 border border-border/50">
                <div>
                  <Label className="cursor-pointer">Notify Eligible Watchers</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">Send alerts to shoppers tracking this item</p>
                </div>
                <Switch 
                  data-testid="notify-watchers-switch"
                  checked={formData.notify_watchers}
                  onCheckedChange={(v) => handleChange('notify_watchers', v)}
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/20 border border-border/50">
                <div>
                  <Label className="cursor-pointer">Send to Agent Queue</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">Agents will post to social channels</p>
                </div>
                <Switch 
                  data-testid="send-to-agents-switch"
                  checked={formData.send_to_agents}
                  onCheckedChange={(v) => handleChange('send_to_agents', v)}
                />
              </div>
              {formData.send_to_agents && (
                <div>
                  <Label>Agent Channels</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {['telegram', 'x', 'tiktok', 'facebook'].map(ch => (
                      <Badge 
                        key={ch}
                        variant={formData.agent_channels.includes(ch) ? 'default' : 'outline'}
                        className="cursor-pointer capitalize transition-colors duration-200"
                        data-testid={`channel-${ch}`}
                        onClick={() => {
                          const channels = formData.agent_channels.includes(ch)
                            ? formData.agent_channels.filter(c => c !== ch)
                            : [...formData.agent_channels, ch];
                          handleChange('agent_channels', channels);
                        }}
                      >
                        {ch === 'x' ? 'X (Twitter)' : ch}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <Label>Hook Text (optional)</Label>
                <Input 
                  data-testid="hook-text-input"
                  placeholder="One-line hook for agent posts..."
                  value={formData.hook_text}
                  onChange={(e) => handleChange('hook_text', e.target.value)}
                  className="mt-2"
                />
              </div>
            </div>
          )}
          
          {step === 5 && (
            <div className="space-y-4" data-testid="wizard-step-review">
              <Card className="bg-muted/20 border-border/50">
                <CardContent className="p-5 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Pool</span>
                    <span className="text-sm font-medium">{formData.pool_title || 'General'}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Discount</span>
                    <span className="text-sm font-medium">{formData.discount_text}</span>
                  </div>
                  {formData.coupon_code && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Coupon</span>
                      <Badge variant="outline" className="font-mono">{formData.coupon_code}</Badge>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Landing URL</span>
                    <span className="text-xs text-primary truncate max-w-[250px]">{formData.landing_url}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Visibility</span>
                    <VisibilityBadge visibility={formData.visibility} />
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Expires</span>
                    <span className="text-sm font-medium">
                      {formData.expires_at ? new Date(formData.expires_at).toLocaleString() : '--'}
                    </span>
                  </div>
                  {(formData.inventory_cap || formData.per_user_cap > 1) && (
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Caps</span>
                      <span className="text-sm">
                        {formData.inventory_cap ? `${formData.inventory_cap} units / ` : ''}{formData.per_user_cap} per user
                      </span>
                    </div>
                  )}
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Distribution</span>
                    <span className="text-sm font-medium">
                      {[
                        formData.notify_watchers && 'Alerts',
                        formData.send_to_agents && `Agents (${formData.agent_channels.join(', ')})`
                      ].filter(Boolean).join(' + ') || 'None'}
                    </span>
                  </div>
                </CardContent>
              </Card>
              <p className="text-xs text-muted-foreground text-center px-4">
                This offer will be visible to eligible watchers on TheDeals.ai immediately after publishing.
              </p>
            </div>
          )}
        </div>
        
        <DialogFooter className="flex justify-between gap-2 sm:justify-between">
          <div>
            {step > 1 && (
              <Button variant="outline" onClick={handleBack} disabled={submitting} data-testid="wizard-back-btn">
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {step < 5 ? (
              <Button onClick={handleNext} data-testid="wizard-next-btn">
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={handleSaveDraft} disabled={submitting} data-testid="wizard-save-draft-btn">
                  Save Draft
                </Button>
                <Button onClick={handlePublish} disabled={submitting} data-testid="wizard-publish-btn">
                  {submitting ? 'Publishing...' : 'Publish Offer'}
                </Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// Main Page Component
// =============================================================================

export default function DashboardDemandOffersPage() {
  const [activeTab, setActiveTab] = useState('pools');
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [pools, setPools] = useState([]);
  const [requests, setRequests] = useState([]);
  const [offers, setOffers] = useState([]);
  const [upstreamMode, setUpstreamMode] = useState(null); // "real" | "mock" | null
  
  // Filters - Pools
  const [poolSearch, setPoolSearch] = useState('');
  const [poolType, setPoolType] = useState('all');
  const [watchersBucket, setWatchersBucket] = useState('any');
  const [poolSortBy, setPoolSortBy] = useState('demand_score');
  const [onlyWithRequests, setOnlyWithRequests] = useState(false);
  
  // Filters - Requests
  const [requestWindow, setRequestWindow] = useState('30d');
  const [requestStatus, setRequestStatus] = useState('all');
  const [requestSort, setRequestSort] = useState('requests');
  
  // Filters - Offers
  const [offerStatus, setOfferStatus] = useState('all');
  const [offerVisibility, setOfferVisibility] = useState('all');
  const [offerSort, setOfferSort] = useState('created_at');
  
  // Drawer/Modal state
  const [selectedPool, setSelectedPool] = useState(null);
  const [poolDrawerOpen, setPoolDrawerOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [offerDrawerOpen, setOfferDrawerOpen] = useState(false);
  const [offerWizardOpen, setOfferWizardOpen] = useState(false);
  const [wizardInitialPool, setWizardInitialPool] = useState(null);
  
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, poolsRes, requestsRes, offersRes] = await Promise.all([
        apiClient.get('/api/dashboard/thedeals/summary').catch(() => ({ data: {} })),
        apiClient.get('/api/dashboard/thedeals/demand-pools', {
          params: { sort: poolSortBy, window: '30d' }
        }).catch(() => ({ data: { pools: [] } })),
        apiClient.get('/api/dashboard/thedeals/offer-requests', {
          params: { window: requestWindow, sort: requestSort }
        }).catch(() => ({ data: { requests: [] } })),
        apiClient.get('/api/dashboard/thedeals/offers', {
          params: { sort: offerSort }
        }).catch(() => ({ data: { offers: [] } })),
      ]);
      
      setSummary(summaryRes.data);
      setPools(poolsRes.data.pools || []);
      setRequests(requestsRes.data.requests || []);
      setOffers(offersRes.data.offers || []);
      
      // Track upstream mode from any response
      const mode = summaryRes.data?.upstream_mode || poolsRes.data?.upstream_mode || null;
      setUpstreamMode(mode);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load demand data', { description: 'Please try refreshing the page' });
    } finally {
      setLoading(false);
    }
  }, [poolSortBy, requestWindow, requestSort, offerSort]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Filtered data
  const filteredPools = useMemo(() => {
    return pools.filter(pool => {
      if (poolSearch && !pool.title.toLowerCase().includes(poolSearch.toLowerCase()) &&
          !(pool.canonical_url || '').toLowerCase().includes(poolSearch.toLowerCase()) &&
          !(pool.domain || '').toLowerCase().includes(poolSearch.toLowerCase())) return false;
      if (poolType !== 'all' && pool.type !== poolType) return false;
      if (watchersBucket !== 'any' && pool.watchers_bucket !== watchersBucket) return false;
      if (onlyWithRequests && pool.requests_7d === 0) return false;
      return true;
    });
  }, [pools, poolSearch, poolType, watchersBucket, onlyWithRequests]);
  
  const filteredRequests = useMemo(() => {
    return requests.filter(req => {
      if (requestStatus !== 'all' && req.status !== requestStatus) return false;
      return true;
    });
  }, [requests, requestStatus]);
  
  const filteredOffers = useMemo(() => {
    return offers.filter(offer => {
      if (offerStatus !== 'all' && offer.status !== offerStatus) return false;
      if (offerVisibility !== 'all' && offer.visibility !== offerVisibility) return false;
      return true;
    });
  }, [offers, offerStatus, offerVisibility]);
  
  const handleViewPool = (pool) => {
    setSelectedPool(pool);
    setPoolDrawerOpen(true);
  };
  
  const handleViewOffer = (offer) => {
    setSelectedOffer(offer);
    setOfferDrawerOpen(true);
  };
  
  const handleCreateOffer = (pool) => {
    setWizardInitialPool(pool);
    setOfferWizardOpen(true);
  };
  
  const handlePauseOffer = async (offerId) => {
    try {
      await apiClient.post(`/api/dashboard/thedeals/offers/${offerId}/pause`, {});
      toast.success('Offer paused', { description: 'Notifications stopped and offer hidden' });
      fetchData();
    } catch (err) {
      if (err.response?.status === 503) {
        toast.error('TheDeals unavailable', { description: 'Integration is currently offline. Try again later.' });
      } else {
        toast.error('Failed to pause offer');
      }
    }
  };
  
  const handleResumeOffer = async (offerId) => {
    try {
      await apiClient.post(`/api/dashboard/thedeals/offers/${offerId}/resume`, {});
      toast.success('Offer resumed', { description: 'Offer is now visible again' });
      fetchData();
    } catch (err) {
      if (err.response?.status === 503) {
        toast.error('TheDeals unavailable', { description: 'Integration is currently offline. Try again later.' });
      } else {
        toast.error('Failed to resume offer');
      }
    }
  };
  
  const handleExportCSV = () => {
    const data = activeTab === 'pools' ? filteredPools : activeTab === 'requests' ? filteredRequests : filteredOffers;
    if (!data.length) { toast.error('No data to export'); return; }
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(row => Object.values(row).map(v => typeof v === 'string' ? `"${v}"` : v).join(','));
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `thedeals_${activeTab}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };
  
  return (
    <div className="min-h-screen bg-background">
      <MerchantHeader />
      
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8" data-testid="demand-offers-page">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Demand & Offers
            </h1>
            <p className="text-sm text-muted-foreground mt-1.5">
              See what shoppers are tracking on TheDeals.ai. Publish targeted offers in minutes.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button 
              variant="outline" 
              size="sm"
              data-testid="export-csv-btn"
              onClick={handleExportCSV}
            >
              <Download className="h-4 w-4 mr-1.5" />
              Export CSV
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              data-testid="view-store-btn"
              onClick={() => window.open('https://thedeals.ai', '_blank')}
            >
              <ExternalLink className="h-4 w-4 mr-1.5" />
              View My Store
            </Button>
            <Button 
              size="sm"
              data-testid="create-offer-btn"
              disabled={upstreamMode === 'mock'}
              onClick={() => { setWizardInitialPool(null); setOfferWizardOpen(true); }}
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Create Offer
            </Button>
          </div>
        </div>
        
        {/* Context Strip */}
        <div className="flex flex-wrap items-center gap-3 mb-6 text-sm">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge 
                  data-testid="connection-status-badge"
                  variant="outline" 
                  className={`gap-1.5 ${summary?.connected ? 'border-emerald-500/50 text-emerald-400 bg-emerald-500/5' : 'border-amber-500/50 text-amber-400 bg-amber-500/5'}`}
                >
                  {summary?.connected ? <CheckCircle className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                  {summary?.connected ? 'Connected to TheDeals' : 'Not Connected'}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                {summary?.connected ? 'Integration is active. Data is syncing.' : 'Configure THEDEALS_BASE_URL and HMAC secret to connect.'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Separator orientation="vertical" className="h-4" />
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Shield className="h-3.5 w-3.5" />
            <span>Demand is aggregated and anonymized. Individual shopper identities are never shown.</span>
          </div>
        </div>
        
        {/* Demo Data Banner (shown when upstream_mode is mock) */}
        {upstreamMode === 'mock' && (
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30 mb-6" data-testid="demo-data-banner">
            <AlertCircle className="h-4 w-4 text-amber-400 flex-shrink-0" />
            <span className="text-sm text-amber-400 font-medium">Demo Data</span>
            <span className="text-xs text-amber-400/80">TheDeals connection unavailable. Showing sample data. Write actions (publish, create offer) are disabled.</span>
          </div>
        )}
        
        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
          <KPICard 
            title="Active Demand Pools" 
            value={summary?.active_pools ?? '--'}
            icon={Users}
            loading={loading}
            testId="kpi-active-pools"
          />
          <KPICard 
            title="Total Watchers" 
            value={summary?.total_watchers ? summary.total_watchers.toLocaleString() : '--'}
            icon={Eye}
            loading={loading}
            testId="kpi-total-watchers"
          />
          <KPICard 
            title="Offer Requests (7d)" 
            value={summary?.offer_requests_7d ?? '--'}
            icon={Target}
            loading={loading}
            testId="kpi-offer-requests"
          />
          <KPICard 
            title="Active Offers" 
            value={summary?.active_offers ?? '--'}
            icon={Zap}
            loading={loading}
            testId="kpi-active-offers"
          />
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="demand-offers-tabs">
          <TabsList className="bg-muted rounded-lg p-1 mb-6">
            <TabsTrigger value="pools" data-testid="tab-pools" className="data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm transition-colors duration-200">
              Demand Pools
            </TabsTrigger>
            <TabsTrigger value="requests" data-testid="tab-requests" className="data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm transition-colors duration-200">
              Offer Requests
            </TabsTrigger>
            <TabsTrigger value="offers" data-testid="tab-offers" className="data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm transition-colors duration-200">
              Offers
            </TabsTrigger>
            <TabsTrigger value="insights" data-testid="tab-insights" disabled className="data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm transition-colors duration-200">
              Insights
              <Badge variant="outline" className="ml-1.5 text-[10px] h-4 px-1">Soon</Badge>
            </TabsTrigger>
          </TabsList>
          
          {/* Demand Pools Tab */}
          <TabsContent value="pools" className="space-y-4">
            <div className="flex flex-wrap items-center gap-3" data-testid="pools-filters">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input 
                  data-testid="pools-search-input"
                  placeholder="Search product, URL, keyword..." 
                  value={poolSearch}
                  onChange={(e) => setPoolSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={poolType} onValueChange={setPoolType}>
                <SelectTrigger className="w-[130px]" data-testid="pool-type-filter">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="item">Item (URL)</SelectItem>
                  <SelectItem value="store">Store</SelectItem>
                  <SelectItem value="category">Category</SelectItem>
                </SelectContent>
              </Select>
              <Select value={watchersBucket} onValueChange={setWatchersBucket}>
                <SelectTrigger className="w-[130px]" data-testid="watchers-bucket-filter">
                  <SelectValue placeholder="Watchers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="1-10">1-10</SelectItem>
                  <SelectItem value="10-25">10-25</SelectItem>
                  <SelectItem value="25-50">25-50</SelectItem>
                  <SelectItem value="50-100">50-100</SelectItem>
                  <SelectItem value="100+">100+</SelectItem>
                </SelectContent>
              </Select>
              <Select value={poolSortBy} onValueChange={setPoolSortBy}>
                <SelectTrigger className="w-[160px]" data-testid="pool-sort-select">
                  <ArrowUpDown className="h-3.5 w-3.5 mr-1.5" />
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="demand_score">Demand Score</SelectItem>
                  <SelectItem value="watchers">Watchers</SelectItem>
                  <SelectItem value="requests">Requests (7d)</SelectItem>
                  <SelectItem value="urgency">Urgency</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                variant={onlyWithRequests ? 'default' : 'outline'} 
                size="sm"
                data-testid="only-with-requests-toggle"
                onClick={() => setOnlyWithRequests(!onlyWithRequests)}
                className="text-xs"
              >
                Only with requests
              </Button>
              <Button variant="outline" size="icon" className="h-9 w-9 ml-auto" onClick={fetchData} data-testid="refresh-button">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            
            <DemandPoolsTable 
              pools={filteredPools}
              loading={loading}
              onViewPool={handleViewPool}
              onCreateOffer={handleCreateOffer}
            />
          </TabsContent>
          
          {/* Offer Requests Tab */}
          <TabsContent value="requests" className="space-y-4">
            <div className="flex flex-wrap items-center gap-3" data-testid="requests-filters">
              <Select value={requestWindow} onValueChange={setRequestWindow}>
                <SelectTrigger className="w-[100px]" data-testid="request-window-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">7 days</SelectItem>
                  <SelectItem value="30d">30 days</SelectItem>
                  <SelectItem value="90d">90 days</SelectItem>
                </SelectContent>
              </Select>
              <Select value={requestStatus} onValueChange={setRequestStatus}>
                <SelectTrigger className="w-[130px]" data-testid="request-status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="fulfilled">Fulfilled</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
              <Select value={requestSort} onValueChange={setRequestSort}>
                <SelectTrigger className="w-[160px]" data-testid="request-sort-select">
                  <ArrowUpDown className="h-3.5 w-3.5 mr-1.5" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="requests">Most Requests</SelectItem>
                  <SelectItem value="urgency">Highest Urgency</SelectItem>
                  <SelectItem value="target_price">Highest Target</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" className="h-9 w-9 ml-auto" onClick={fetchData} data-testid="requests-refresh-button">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            
            <OfferRequestsTable 
              requests={filteredRequests}
              loading={loading}
              onCreateOffer={(req) => handleCreateOffer({ pool_id: req.pool_id, title: req.pool_title || req.title })}
            />
          </TabsContent>
          
          {/* Offers Tab */}
          <TabsContent value="offers" className="space-y-4">
            <div className="flex flex-wrap items-center gap-3" data-testid="offers-filters">
              <Select value={offerStatus} onValueChange={setOfferStatus}>
                <SelectTrigger className="w-[130px]" data-testid="offer-status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
              <Select value={offerVisibility} onValueChange={setOfferVisibility}>
                <SelectTrigger className="w-[130px]" data-testid="offer-visibility-filter">
                  <SelectValue placeholder="Visibility" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="watchers">Watchers</SelectItem>
                  <SelectItem value="public">Public</SelectItem>
                </SelectContent>
              </Select>
              <Select value={offerSort} onValueChange={setOfferSort}>
                <SelectTrigger className="w-[150px]" data-testid="offer-sort-select">
                  <ArrowUpDown className="h-3.5 w-3.5 mr-1.5" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at">Newest</SelectItem>
                  <SelectItem value="expires_soon">Ends Soon</SelectItem>
                  <SelectItem value="clicks">Most Clicks</SelectItem>
                  <SelectItem value="ctr">Highest CTR</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" className="h-9 w-9 ml-auto" onClick={fetchData} data-testid="offers-refresh-button">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            
            <OffersTable 
              offers={filteredOffers}
              loading={loading}
              onViewOffer={handleViewOffer}
              onPauseOffer={handlePauseOffer}
              onResumeOffer={handleResumeOffer}
            />
          </TabsContent>
          
          {/* Insights Tab (Coming Soon) */}
          <TabsContent value="insights" className="space-y-4">
            <Card className="bg-card border-border/80 rounded-xl">
              <CardContent className="p-12 text-center">
                <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-semibold mb-2">Insights Coming Soon</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  Detailed analytics on demand trends, offer performance, and conversion insights will be available here.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        {/* Drawers & Modals */}
        <PoolDetailsDrawer 
          pool={selectedPool}
          open={poolDrawerOpen}
          onClose={() => setPoolDrawerOpen(false)}
          onCreateOffer={handleCreateOffer}
        />
        
        <OfferDetailsDrawer
          offer={selectedOffer}
          open={offerDrawerOpen}
          onClose={() => setOfferDrawerOpen(false)}
          onPause={handlePauseOffer}
          onResume={handleResumeOffer}
        />
        
        <OfferBuilderWizard 
          open={offerWizardOpen}
          onClose={() => setOfferWizardOpen(false)}
          initialPool={wizardInitialPool}
          onSuccess={fetchData}
        />
      </div>
    </div>
  );
}
